import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Calendar, Wallet, PenLine, MessageSquare, X, Link2, Send } from 'lucide-react';
import { agentAppAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import SignaturePad from '../../components/agent/SignaturePad';

const fmt = (n) => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));

// Standalone "Collect Payment" — records money against the current outstanding balance
// without touching the subscription period/expiry, unlike AgentCollectPaymentPage ("Renew")
// which always generates a new renewal. Mirrors the admin desktop's CollectPaymentPanel.
export default function AgentCollectBalancePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setHeader } = useOutletContext();
  const { user } = useAuth();
  const [customer, setCustomer] = useState(null);
  const [amount, setAmount] = useState('');
  const [discount, setDiscount] = useState('0');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [notes, setNotes] = useState('');
  const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().slice(0, 16));
  const [signature, setSignature] = useState(null);
  const [signOpen, setSignOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [saving, setSaving] = useState(null); // null | 'plain'
  const [sendingLink, setSendingLink] = useState(false);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkNumber, setLinkNumber] = useState('');

  const isAdmin = user?.role === 'admin';
  const perms = user?.permissions || {};
  const canAdjust = isAdmin || perms.canAdjustBalance;
  const canBackdate = isAdmin || perms.addPaymentOnAnyDate;

  const load = useCallback(async () => {
    try {
      const { data } = await agentAppAPI.getCustomer(id);
      setCustomer(data.data);
      setAmount(String(data.data.outstandingBalance || ''));
      setLinkNumber(data.data.mobile || '');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load customer');
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setHeader({ title: 'Collect Payment', showBack: true }); }, [setHeader]);

  if (!customer) return <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading…</div>;

  // Discount is a write-off on top of the amount collected — both reduce the outstanding
  // balance independently (paymentService.collectPayment's additiveDiscount mode).
  // A negative result is an ADVANCE: the backend stores it as a negative due, and the next
  // month's bill nets off against it; a positive result is the due still left to collect.
  const newBalance = (customer.outstandingBalance || 0) - Number(amount || 0) - Number(discount || 0);

  // Sends a payment link on WhatsApp to a number the agent picks — no payment is recorded;
  // the agent records it once the money actually arrives. Defaults to the customer's own
  // mobile but is editable, since it's often a relative or someone else nearby who actually
  // pays (customer.mobile may also be missing/wrong).
  //
  // Only ever the payment-gateway link (UPI + card + net banking on a secure page, and the
  // money lands in the merchant account the routing rules pick, so the payment posts against
  // the customer by itself). There is deliberately no raw upi:// fallback: that settles into
  // the payee's own VPA without ever touching the gateway, leaving the collection invisible
  // to the CRM. No gateway configured means no online link — cash, or record it manually.
  //
  // The link always charges the customer's LIVE outstanding balance — the amount typed above
  // is what the agent expects to collect in hand, and the gateway page re-reads the real due
  // server-side — so the message quotes the outstanding balance, not the typed amount, or
  // whoever receives it would be told one figure and shown another.
  const gatewayLinkReady = customer.paymentLinkGatewayEnabled && customer.paymentLink && (customer.outstandingBalance || 0) > 0;

  const openLinkModal = () => {
    if (!gatewayLinkReady) {
      toast.error((customer.outstandingBalance || 0) > 0
        ? 'Payment gateway is not configured — collect in cash or record the payment manually'
        : 'No outstanding balance to collect');
      return;
    }
    setLinkModalOpen(true);
  };

  const sendOnlineLink = async () => {
    const digits = linkNumber.replace(/\D/g, '').slice(-10);
    if (digits.length !== 10) { toast.error('Enter a valid 10-digit mobile number'); return; }

    const text = `Dear ${customer.name}, please pay ₹${fmt(customer.outstandingBalance)} using this secure link:\n${customer.paymentLink}`;

    setSendingLink(true);
    try {
      await agentAppAPI.sendWhatsAppMessage(id, text, digits);
      toast.success('Payment link sent on WhatsApp');
      setLinkModalOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send payment link');
    } finally {
      setSendingLink(false);
    }
  };

  const submit = async () => {
    if (!amount || Number(amount) <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setSaving('plain');
    try {
      const payload = {
        amount: Number(amount),
        discount: Number(discount) || 0,
        paymentMethod,
        notes,
        signature: signature || undefined,
      };
      if (canBackdate) payload.paymentDate = new Date(paymentDate).toISOString();

      const { data } = await agentAppAPI.collectBalance(id, payload);
      const newPayment = data.data;
      toast.success(data.message || 'Payment recorded');
      navigate(`/agent/receipt/${newPayment._id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Payment failed');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div style={{ padding: 14 }}>
      <div style={{ textAlign: 'center', color: customer.outstandingBalance > 0 ? '#ea580c' : '#16a34a', fontWeight: 700, fontSize: '1.05rem', margin: '4px 0 20px', lineHeight: 1.4 }}>
        {customer.outstandingBalance > 0
          ? `You need to collect ₹${fmt(customer.outstandingBalance)} from ${customer.name}`
          : customer.outstandingBalance < 0
            ? `${customer.name} has an advance of ₹${fmt(-customer.outstandingBalance)}`
            : `No due pending from ${customer.name} — any amount collected becomes advance`}
      </div>

      <div style={{ textAlign: 'center', marginBottom: 12 }}>
        <label style={{ fontSize: '.8rem', color: 'var(--text-muted)', display: 'block' }}>Paid Amount (in ₹)</label>
        <input
          type="number" value={amount}
          onChange={(e) => setAmount(e.target.value)}
          style={{ fontSize: '1.8rem', fontWeight: 700, textAlign: 'center', border: 'none', borderBottom: '2px solid var(--border)', width: 160, background: 'transparent', color: '#dc2626' }}
        />
      </div>

      <Row label="Discount (in ₹)">
        <input
          type="number" value={discount} disabled={!canAdjust}
          onChange={(e) => setDiscount(e.target.value)}
          className="form-control" style={{ width: 100, textAlign: 'right' }}
        />
      </Row>
      <Row label="New Balance" bold>
        {newBalance > 0 && <span style={{ color: '#dc2626' }}>Due left ₹{fmt(newBalance)}</span>}
        {newBalance < 0 && <span style={{ color: '#16a34a' }}>Advance ₹{fmt(-newBalance)}</span>}
        {newBalance === 0 && <span style={{ color: '#16a34a' }}>₹0 — fully settled</span>}
      </Row>
      {newBalance < 0 && (
        <div style={{ fontSize: '.75rem', color: 'var(--text-muted)', padding: '6px 0' }}>
          The advance will be adjusted automatically against next month&apos;s bill.
        </div>
      )}

      <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', margin: '16px 0' }}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRight: '1px solid var(--border)' }}>
          <Calendar size={16} color="var(--text-muted)" />
          <input
            type="datetime-local" value={paymentDate} disabled={!canBackdate}
            onChange={(e) => setPaymentDate(e.target.value)}
            style={{ border: 'none', background: 'transparent', fontSize: '.78rem', width: '100%', padding: 0, color: 'inherit' }}
          />
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px' }}>
          <Wallet size={16} color="var(--text-muted)" />
          <select
            value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}
            style={{ border: 'none', background: 'transparent', fontSize: '.82rem', fontWeight: 600, width: '100%', padding: 0, color: 'inherit' }}
          >
            <option value="cash">CASH</option>
            <option value="upi">UPI</option>
            <option value="payment_gateway">PAYMENT GATEWAY</option>
            <option value="bank_transfer">BANK TRANSFER</option>
            <option value="cheque">CHEQUE</option>
          </select>
        </div>
      </div>

      <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginBottom: 24 }}>
        <button
          type="button" onClick={() => setSignOpen(true)}
          style={{
            flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
            borderRight: '1px solid var(--border)', borderTop: 'none', borderBottom: 'none', borderLeft: 'none',
            background: 'none', cursor: 'pointer', fontSize: '.82rem', color: signature ? '#16a34a' : 'inherit'
          }}
        >
          <PenLine size={16} color={signature ? '#16a34a' : 'var(--text-muted)'} />
          {signature ? 'Signed ✓' : 'Add Sign'}
        </button>
        <button
          type="button" onClick={() => setNoteOpen(true)}
          style={{
            flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
            border: 'none', background: 'none', cursor: 'pointer', fontSize: '.82rem', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis'
          }}
        >
          <MessageSquare size={16} color="var(--text-muted)" />
          {notes ? notes : 'Add Note'}
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <button
          onClick={submit} disabled={!!saving}
          style={btnStyle(saving)}
        >
          {saving === 'plain' ? 'Recording…' : 'Record'}
        </button>
        <button
          onClick={openLinkModal} disabled={sendingLink || !!saving}
          style={{
            width: '100%', background: '#fff',
            color: gatewayLinkReady ? '#16a34a' : 'var(--text-muted)',
            border: `1.5px solid ${gatewayLinkReady ? '#16a34a' : 'var(--border)'}`,
            borderRadius: 24, padding: '12px 0', fontWeight: 700, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            opacity: sendingLink ? 0.6 : 1,
          }}
        >
          {/* Spells out the amount because the gateway link always charges the live due rather
              than whatever was typed above. Greyed (but still tappable, so the tap can explain
              itself) when no gateway is available — there is no UPI fallback to offer. */}
          <Link2 size={16} /> {sendingLink ? 'Sending…'
            : gatewayLinkReady ? `Send Payment Link — ₹${fmt(customer.outstandingBalance)}`
            : 'Send Payment Link'}
        </button>
      </div>

      {signOpen && (
        <SignaturePad value={signature} onSave={setSignature} onClose={() => setSignOpen(false)} />
      )}

      {noteOpen && (
        <div onClick={() => setNoteOpen(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex',
          alignItems: 'flex-end', justifyContent: 'center', zIndex: 100
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: '#fff', borderRadius: '16px 16px 0 0', padding: 18, width: '100%', maxWidth: 480
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontWeight: 700 }}>Add Note</div>
              <button onClick={() => setNoteOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
            </div>
            <textarea
              className="form-control" rows={3} autoFocus value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            <button
              onClick={() => setNoteOpen(false)}
              style={{ width: '100%', marginTop: 12, background: 'var(--bg-sidebar)', color: '#fff', border: 'none', borderRadius: 22, padding: '11px 0', fontWeight: 700, cursor: 'pointer' }}
            >
              Done
            </button>
          </div>
        </div>
      )}

      {linkModalOpen && (
        <div onClick={() => !sendingLink && setLinkModalOpen(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex',
          alignItems: 'flex-end', justifyContent: 'center', zIndex: 100
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: '#fff', borderRadius: '16px 16px 0 0', padding: 18, width: '100%', maxWidth: 480
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <div style={{ fontWeight: 700 }}>Send Payment Link</div>
              <button onClick={() => setLinkModalOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
            </div>
            <div style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginBottom: 12 }}>
              Charges the live outstanding balance — ₹{fmt(customer.outstandingBalance)}
            </div>
            <label style={{ fontSize: '.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
              Send to (mobile number)
            </label>
            <input
              type="tel" autoFocus value={linkNumber}
              onChange={(e) => setLinkNumber(e.target.value)}
              placeholder="10-digit mobile number"
              className="form-control"
            />
            <button
              onClick={sendOnlineLink} disabled={sendingLink}
              style={{ width: '100%', marginTop: 12, background: 'var(--bg-sidebar)', color: '#fff', border: 'none', borderRadius: 22, padding: '11px 0', fontWeight: 700, cursor: sendingLink ? 'default' : 'pointer', opacity: sendingLink ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            >
              <Send size={16} /> {sendingLink ? 'Sending…' : 'Send on WhatsApp'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function btnStyle(saving) {
  return {
    width: '100%', background: 'var(--bg-sidebar)', color: '#fff', border: 'none', borderRadius: 24,
    padding: '12px 0', fontWeight: 700, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1,
  };
}

function Row({ label, children, bold }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ fontSize: '.85rem', fontWeight: bold ? 700 : 500 }}>{label}</span>
      <span style={{ fontWeight: bold ? 700 : 500 }}>{children}</span>
    </div>
  );
}
